import type {
  FixFee,
  Interest,
  PenaltyClause,
  TermsAndConditions,
  TermsAndConditionsSettings,
} from 'modules/Settings/Configuration/api';
import { TermsAndConditionsVariablesSchema } from 'modules/Settings/Configuration/api';
import { z } from 'zod/v4';

// The form validates exactly what the write endpoint accepts, plus the
// read-only fields the card still has to render. Seeding it from a legacy row
// that breaks one of those bounds surfaces a field error rather than a 422.
const DraftTermsAndConditionsSchema = TermsAndConditionsVariablesSchema.extend({
  hasPDF: z.boolean(),
  pdfFilename: z.string(),
  pdfUrl: z.string(),
  deletable: z.boolean(),
});

export const TermsAndConditionsFormSchema = z.object({
  termsAndConditions: z.array(DraftTermsAndConditionsSchema),
  sendTOS: z.boolean(),
  ignoreLateFees: z.boolean(),
});

export type TermsAndConditionsSet = z.infer<typeof DraftTermsAndConditionsSchema>;
export type TermsAndConditionsFormValues = z.infer<typeof TermsAndConditionsFormSchema>;

// One notch below the Mantine default, so the three columns of a row stay
// compact enough to sit side by side.
export const INPUT_SIZE = 'xs';

export const INITIAL_VALUES: TermsAndConditionsFormValues = {
  termsAndConditions: [],
  sendTOS: false,
  ignoreLateFees: false,
};

const defaultInterest = (): Interest => ({ value: 0, period: 'annual' });
const defaultFixFee = (): FixFee => ({ value: 0 });
const defaultPenaltyClause = (): PenaltyClause => ({
  type: 'percentage',
  value: 0,
  minimum: 0,
});

export type FeeField = 'interest' | 'fixFee' | 'penaltyClause';

export type RememberedFees = {
  interest: Interest;
  fixFee: FixFee;
  penaltyClause: PenaltyClause;
};

export const initialRememberedFees = (): RememberedFees => ({
  interest: defaultInterest(),
  fixFee: defaultFixFee(),
  penaltyClause: defaultPenaltyClause(),
});

// Switching a fee off nulls the whole block, which is what the wire expects but
// would otherwise throw away the numbers behind it. Holding on to them means
// switching back on restores what was there rather than resetting to zero.
export function nextFeeValue<K extends FeeField>(
  remembered: RememberedFees,
  field: K,
  current: RememberedFees[K] | null,
  enabled: boolean,
): { value: RememberedFees[K] | null; remembered: RememberedFees } {
  const kept: RememberedFees = current == null ? remembered : { ...remembered, [field]: current };

  return { value: enabled ? (kept[field] as RememberedFees[K]) : null, remembered: kept };
}

export const emptySet = (name: string): TermsAndConditionsSet => ({
  name,
  default: false,
  minimumPaymentTermsDelay: 30,
  useBelgianLaw: false,
  interest: null,
  fixFee: null,
  penaltyClause: null,
  hasPDF: false,
  pdfFilename: '',
  pdfUrl: '',
  deletable: true,
});

export const toFormValues = (
  settings: TermsAndConditionsSettings,
): TermsAndConditionsFormValues => ({
  termsAndConditions: settings.termsAndConditions.map((set: TermsAndConditions) => ({ ...set })),
  sendTOS: settings.sendTOS,
  ignoreLateFees: settings.ignoreLateFees,
});

// The write endpoint owns neither the document nor the derived deletable flag.
export const toUpdateVariables = (values: TermsAndConditionsFormValues) => ({
  sendTOS: values.sendTOS,
  ignoreLateFees: values.ignoreLateFees,
  termsAndConditions: values.termsAndConditions.map(
    ({
      hasPDF: _hasPDF,
      pdfFilename: _pdfFilename,
      pdfUrl: _pdfUrl,
      deletable: _deletable,
      ...set
    }) => set,
  ),
});
